import React from 'react';
import { CloseOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useSensorMaintenanceLens } from '../context/SensorMaintenanceLensContext';
import type { SensorMaintenanceState } from '../types/sensorMaintenanceLens.types';

interface SensorMaintenanceLensSidebarProps {
	onClose: () => void;
	width?: number;
}

interface StateRowConfig {
	state: SensorMaintenanceState;
	label: string;
	color: string;
}

const STATE_ROWS: StateRowConfig[] = [
	{ state: 'active', label: '# Reporting on Time', color: '#1E96BE' },
	{ state: 'inactive', label: '# Late Reporting', color: '#F46A6A' },
	{ state: 'withoutData', label: '# No Data (24h)', color: '#B6BEC9' },
];

const Divider: React.FC = () => <div className="h-px w-full bg-[#EDF1F5]" />;

const SensorMaintenanceLensSidebar: React.FC<SensorMaintenanceLensSidebarProps> = ({
	onClose,
	width = 470,
}) => {
	const { loading, resume, hiddenStates, toggleStateVisibility } = useSensorMaintenanceLens();

	const renderValue = (value: React.ReactNode) =>
		loading ? <span className="text-[#9AA8BC]">—</span> : value;

	return (
		<>
			<style>{`
				@keyframes sensorMaintenanceLensSlideIn {
					from { transform: translateX(100%); }
					to { transform: translateX(0); }
				}
			`}</style>
			<div
				className="fixed z-[1000] flex justify-end pointer-events-none"
				style={{ top: '85px', right: '10px', bottom: '10px' }}
			>
				<div
					className="relative flex h-fit flex-col overflow-hidden rounded-md bg-white pointer-events-auto shadow-[-2px_0_8px_rgba(0,0,0,0.15)]"
					style={{ width, animation: 'sensorMaintenanceLensSlideIn 0.3s ease-out' }}
				>
					<div className="flex flex-shrink-0 items-center justify-between px-6 pb-3 pt-5">
						<h2 className="m-0 text-xl font-bold uppercase tracking-wide text-[#1A1A2E]">
							Sensor Maintenance Lens
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="border-none bg-transparent p-0 text-gray-500 outline-none transition-colors hover:text-gray-800 focus:outline-none"
							aria-label="Close Sensor Maintenance Lens"
						>
							<CloseOutlined />
						</button>
					</div>

					<div className="flex flex-col px-6 pb-5">
						<div className="grid grid-cols-[1fr_90px_96px] items-center py-4">
							<span className="text-base font-semibold text-[#74788D]">
								Total number of devices
							</span>
							<span className="text-base font-bold text-[#1A1A2E]">
								{renderValue(resume.totalNumberOfDevices)}
							</span>
							<span />
						</div>

						<Divider />

						<div className="grid grid-cols-[1fr_90px_96px] items-center py-4">
							<span className="text-base font-semibold text-[#74788D]">System throughput</span>
							<span className="text-base font-bold text-[#1A1A2E]">
								{renderValue(resume.systemThroughput)} %
							</span>
							<span />
						</div>

						{STATE_ROWS.map((row) => {
							const hidden = hiddenStates.has(row.state);
							return (
								<React.Fragment key={row.state}>
									<Divider />
									<div className="grid grid-cols-[1fr_90px_96px] items-center py-4">
										<div className="flex items-center gap-3">
											<span
												className="h-3 w-3 flex-shrink-0 rounded-full"
												style={{ backgroundColor: row.color }}
											/>
											<span className="text-base font-semibold text-[#74788D]">{row.label}</span>
										</div>
										<span className="text-base font-bold text-[#1E96BE] underline">
											{renderValue(resume[row.state])}
										</span>
										<button
											type="button"
											onClick={() => toggleStateVisibility(row.state)}
											className="flex w-fit items-center justify-self-start border-none bg-transparent p-0 text-[#1E96BE] outline-none transition-opacity hover:opacity-80 focus:outline-none focus-visible:outline-none"
											aria-pressed={hidden}
											aria-label={hidden ? 'Show' : 'Hide'}
										>
											{hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
										</button>
									</div>
								</React.Fragment>
							);
						})}
					</div>
				</div>
			</div>
		</>
	);
};

export default SensorMaintenanceLensSidebar;
